import { useEffect, useState } from "react";

const HOUR_MS = 3_600_000;

/** ponytail: the open clock moves once a minute. Hour text is rounded to 0.1 h. */
export const useClockNow = (): number => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  return now;
};

/**
 * Closed clock hours, plus the open segment while clockStartedAt is set.
 * A missing clock and a missing closed total is "not started".
 */
export const liveElapsedHours = (
  elapsedHours: number | null | undefined,
  clockStartedAt: string | null | undefined,
  nowMs: number
): number | null => {
  const closed =
    typeof elapsedHours === "number" && Number.isFinite(elapsedHours)
      ? elapsedHours
      : null;
  const started =
    typeof clockStartedAt === "string" && clockStartedAt !== ""
      ? Date.parse(clockStartedAt)
      : Number.NaN;
  const open = Number.isFinite(started)
    ? Math.max(0, nowMs - started) / HOUR_MS
    : 0;
  if (closed == null && !Number.isFinite(started)) {
    return null;
  }
  return (closed ?? 0) + open;
};

/**
 * Empty SLA is never past due.
 * A stopped clock uses the API flag only. A running clock can cross the SLA
 * after the response was sent.
 */
export const showPastSla = ({
  slaHours,
  pastSla,
  elapsedHours,
  clockStartedAt,
  nowMs,
}: {
  slaHours: number | null | undefined;
  pastSla: boolean;
  elapsedHours: number | null | undefined;
  clockStartedAt: string | null | undefined;
  nowMs: number;
}): boolean => {
  if (typeof slaHours !== "number" || !Number.isFinite(slaHours)) {
    return false;
  }
  if (pastSla) {
    return true;
  }
  if (!clockStartedAt) {
    return false;
  }
  const live = liveElapsedHours(elapsedHours, clockStartedAt, nowMs);
  return live != null && live > slaHours;
};

/** Whole minutes, then hours, then days. Null when the timestamp does not parse. */
export const formatRelativeAgo = (
  actedAt: string,
  nowMs: number
): string | null => {
  const then = Date.parse(actedAt);
  if (!Number.isFinite(then)) {
    return null;
  }
  const minutes = Math.floor(Math.max(0, nowMs - then) / 60_000);
  if (minutes < 60) {
    const unit = minutes === 1 ? "minute" : "minutes";
    return `${minutes} ${unit} ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const unit = hours === 1 ? "hour" : "hours";
    return `${hours} ${unit} ago`;
  }
  const days = Math.floor(hours / 24);
  const unit = days === 1 ? "day" : "days";
  return `${days} ${unit} ago`;
};

export const formatClockHours = (hours: number | null | undefined): string => {
  if (hours == null || !Number.isFinite(hours)) {
    return "—";
  }
  const rounded = Math.round(hours * 10) / 10;
  const text = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return `${text} h`;
};

/**
 * Human age for list rows: "12m ago", "18h ago", "5d ago", "2w ago", "3mo ago".
 * ponytail: moment's "en-short" locale is only registered in src/index.js, so
 * Storybook and jest would fall back to "18 hours ago". Thresholds live here.
 */
export const formatElapsedAgo = (hours: number | null | undefined): string => {
  if (hours == null || !Number.isFinite(hours)) {
    return "—";
  }
  const minutes = Math.round(hours * 60);
  if (minutes < 1) {
    return "just now";
  }
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const wholeHours = Math.round(hours);
  if (wholeHours < 24) {
    return `${wholeHours}h ago`;
  }
  const days = Math.round(hours / 24);
  if (days < 7) {
    return `${days}d ago`;
  }
  if (days < 30) {
    return `${Math.round(days / 7)}w ago`;
  }
  return `${Math.round(days / 30)}mo ago`;
};

/** overrideRate is a ratio. Null means the denominator was 0. */
export const formatOverrideRate = (rate: number | null | undefined): string => {
  if (rate == null || !Number.isFinite(rate)) {
    return "—";
  }
  const percent = Math.round(rate * 1000) / 10;
  const text = Number.isInteger(percent) ? String(percent) : percent.toFixed(1);
  return `${text}%`;
};

export const oldestLiveHours = (
  items: Array<{
    elapsedHours: number | null | undefined;
    clockStartedAt?: string | null;
  }>,
  nowMs: number,
  fallback: number | null | undefined
): number | null => {
  const ages = items.flatMap((item) => {
    const age = liveElapsedHours(item.elapsedHours, item.clockStartedAt, nowMs);
    return age == null ? [] : [age];
  });
  if (ages.length === 0) {
    return fallback ?? null;
  }
  return Math.max(...ages);
};
